/**
 * TRADE BOOK — the signed-in user's own trade history.
 *
 *   Trade Cycles  one row per execution (first fill -> square-off), with its fills summarised
 *   Fills         one row per actual fill (db.trades), PAPER and LIVE alike
 *
 * Every row comes from /api/trade-book/*, which the server restricts to the caller. Dates and
 * times are shown in IST; the stored UTC instants stay authoritative. The legacy simulator journal
 * is a separate ADMIN / dev tab and is never merged with real executions.
 */
import { scannerNameDiffers } from "@/lib/scannerName";
import React, { useEffect, useState, useCallback } from "react";
import api, { detailText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/lib/permissions";
import { inr, inrPnl, num, istDate, istTime, istYmd, dateTime } from "@/lib/format";
import { scopeParams } from "@/components/BookScope";
import ReportFilters, { DEFAULT_REPORT_FILTERS, reportParams } from "@/components/ReportFilters";
import PageCount, { PAGE_STEP } from "@/components/PageCount";
import { BookOpen, X, Layers, ClipboardList, FileText, ListOrdered } from "lucide-react";
import { qtyLabel } from "@/lib/qty";
import { exitText } from "@/lib/labels";
import { visibleInterval } from "@/lib/poll";

const STATUS_COLORS = {
  OPEN: "bg-[#00C805]/15 text-[#00C805]", EXITING: "bg-[#FFCC00]/15 text-[#FFCC00]",
  COMPLETE: "bg-[#7c3aed]/20 text-[#c4b5fd]", CANCELLED: "bg-[#475569]/25 text-slate-400",
  ERROR: "bg-[#FF6b62]/15 text-[#FF6b62]",
};

function Cell({ label, value, cls }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xs mono ${cls || "text-slate-200"}`}>{value}</div>
    </div>
  );
}

const SideBadge = ({ side }) => (
  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${side === "SELL" ? "bg-[#FF6b62]/15 text-[#FF6b62]" : "bg-[#5b9bff]/15 text-[#5b9bff]"}`}>{side}</span>
);

function FillsTable({ fills, testid = "fills-table" }) {
  const heads = ["Date", "Time (IST)", "Trade", "Execution", "Env", "Exchange", "Symbol", "Side", "Action",
    "Level", "Pair · Leg", "Qty", "Price", "Value", "Order ID"];
  // every executed contract of a trade's pair (in this list), so all its fills say when the name is the scanner pair
  const pairSyms = {};
  for (const f of fills) {
    const k = `${f.strategy_id}:${f.pair_no}`;
    (pairSyms[k] = pairSyms[k] || []).push(f.tradingsymbol || f.symbol);
  }
  return (
    <div className="overflow-auto rounded-sm border border-[#2A2F3A]" data-testid={testid}>
      <table className="w-full text-[11px] mono border-collapse">
        <thead className="bg-[#1A1D24] text-slate-400"><tr>
          {heads.map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>
          {fills.length === 0 ? (
            <tr><td colSpan={heads.length} className="px-3 py-4 text-center text-slate-600" data-testid="fills-empty">No fills match these filters.</td></tr>
          ) : fills.map((t) => (
            <tr key={t.id} className="gz-row" data-testid={`fill-row-${t.id}`}>
              <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{istDate(t.trade_date || istYmd(t.executed_at))}</td>
              <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{t.trade_time || istTime(t.executed_at)}</td>
              <td className="px-2 py-1.5 text-fg whitespace-nowrap">{t.trade_name || "—"}{scannerNameDiffers(t.trade_name, pairSyms[`${t.strategy_id}:${t.pair_no}`] || [t.tradingsymbol || t.symbol]) && <span
                className="ml-1 text-[9px] text-[var(--term-muted)]" data-testid={`fill-scanner-name-${t.id}`}
                title="The trade is named after its scanner pair; this fill's executed contract is in the Symbol column">(scanner pair)</span>}{t.strategy_id && <div className="text-[10px] mono text-slate-400" title={t.strategy_id}>#{String(t.strategy_id).slice(0, 8)}</div>}</td>
              <td className="px-2 py-1.5 text-[#5b9bff] whitespace-nowrap">{t.execution_id || "—"}</td>
              <td className="px-2 py-1.5 text-slate-500">{t.trading_environment}</td>
              <td className="px-2 py-1.5 text-slate-400">{t.exchange || "—"}</td>
              <td className={`px-2 py-1.5 whitespace-nowrap ${t.side === "SELL" ? "text-[#FF6b62]" : "text-[#5b9bff]"}`}>{t.tradingsymbol || t.symbol}</td>
              <td className="px-2 py-1.5"><SideBadge side={t.side} /></td>
              <td className="px-2 py-1.5 text-slate-300" title={t.exit_reason || undefined}>{t.action === "EXIT" ? (t.exit_reason ? exitText(t.exit_reason) : "EXIT") : t.action}</td>
              <td className="px-2 py-1.5 text-slate-300">{t.level_no != null ? `L${t.level_no}` : "—"}</td>
              <td className="px-2 py-1.5 text-slate-400">{t.pair_no != null ? `P${t.pair_no} · ${t.rescue_side ? `rescue ${t.rescue_side}` : t.leg_role || "—"}` : "—"}</td>
              <td className="px-2 py-1.5 text-right whitespace-nowrap" data-testid="fill-qty">{qtyLabel(t)}</td>
              <td className="px-2 py-1.5 text-right text-slate-100">{t.price != null ? num(t.price, 2) : "—"}</td>
              <td className="px-2 py-1.5 text-right text-slate-300">{t.value != null ? inr(t.value) : "—"}</td>
              <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{t.internal_order_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailDrawer({ id, onClose }) {
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [tab, setTab] = useState("summary");
  useEffect(() => {
    api.get(`/paper/v25/executions/${id}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    api.get(`/trade-book/cycles/${id}`).then((r) => setCycle(r.data)).catch(() => setCycle({ fills: [] }));
  }, [id]);
  const exe = data?.execution;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="w-[860px] max-w-full h-full overflow-auto bg-[#12151C] border-l border-[#2A2F3A] p-5" onClick={(e) => e.stopPropagation()} data-testid="execution-drawer">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold">{exe?.trade_instance_name} · {exe?.underlying} <span className="text-slate-500 mono text-[11px]">{id}</span></div>
          <button onClick={onClose} data-testid="close-drawer"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        {!data ? <div className="text-xs text-slate-500">Loading…</div> : data.error ? <div className="text-xs text-[#FF6b62]" data-testid="execution-not-found">Not found</div> : (
          <>
            <div className="flex gap-1 mb-3">
              {[["summary", "Summary", FileText], ["fills", "Fills", ListOrdered], ["levels", "Levels", Layers], ["audit", "Decision Audit", ClipboardList]].map(([k, lbl, Icon]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-sm flex items-center gap-1 ${tab === k ? "bg-[#2563EB]" : "bg-[#232730] border border-[#2A2F3A] text-slate-400"}`}>
                  <Icon className="h-3 w-3" /> {lbl}
                </button>
              ))}
            </div>

            {tab === "summary" && (
              <div className="term-panel rounded-sm p-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                <Cell label="Status" value={exe.status} />
                <Cell label="Environment" value={exe.trading_environment || exe.environment} />
                <Cell label="Engine" value={exe.engine} />
                <Cell label="Market" value={exe.market} />
                <Cell label="Underlying" value={exe.underlying} />
                <Cell label="Direction" value={exe.direction} />
                <Cell label="SELL" value={exe.sell_contract} cls="text-[#FF6b62] text-[10px]" />
                <Cell label="BUY" value={exe.buy_contract} cls="text-[#5b9bff] text-[10px]" />
                <Cell label="Strategy Ver" value={exe.strategy_version} />
                <Cell label="Allocated" value={inr(exe.allocated_capital)} />
                <Cell label="Max Funds Used" value={inr(exe.max_funds_used)} />
                <Cell label="Levels Entered" value={`${exe.levels_entered} (max L${exe.maximum_level})`} />
                <Cell label="Realised P&L" value={inr(exe.realised_pnl)} cls={exe.realised_pnl >= 0 ? "text-[#00C805]" : "text-[#FF6b62]"} />
                <Cell label="P&L @ Exit Decision" value={inr(exe.pnl_at_exit_decision)} />
                <Cell label="Target @ Exit" value={exe.target_at_exit != null ? inr(exe.target_at_exit) : "—"} />
                <Cell label="A25 @ Exit (indicator)" value={inr(exe.a25_indicator_at_exit)} cls="text-slate-400" />
                <Cell label="Exit Reason" value={exitText(exe.exit_reason)} />
                <Cell label="Started (IST)" value={dateTime(exe.started_at)} />
                <Cell label="Completed (IST)" value={exe.completed_at ? dateTime(exe.completed_at) : "—"} />
                <Cell label="Fills" value={cycle?.cycle?.fill_summary ? `${cycle.cycle.fill_summary.fills} (B ${cycle.cycle.fill_summary.buy.fills} / S ${cycle.cycle.fill_summary.sell.fills})` : "—"} />
                <Cell label="Broker Order ID" value={exe.broker_order_id || "null (PAPER)"} />
              </div>
            )}

            {tab === "fills" && (!cycle ? <div className="text-xs text-slate-500">Loading…</div> : <FillsTable fills={cycle.fills || []} testid="drawer-fills" />)}

            {tab === "levels" && (
              <div className="overflow-auto rounded-sm border border-[#2A2F3A]">
                <table className="w-full text-[11px] mono border-collapse">
                  <thead className="bg-[#1A1D24] text-slate-400"><tr>
                    {["Lvl", "Status", "Qty", "Funds", "SELL entry", "BUY entry", "Entry spread", "SELL exit", "BUY exit", "Exit spread", "P&L", "Entry (IST)", "Exit (IST)"].map((h) => <th key={h} className="px-2 py-1.5 text-right first:text-left font-semibold whitespace-nowrap">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {data.levels.map((l) => (
                      <tr key={l.id} className="gz-row" data-testid={`exlevel-${l.level_number}`}>
                        <td className="px-2 py-1.5 text-left font-bold text-fg">{l.level_number}</td>
                        <td className="px-2 py-1.5 text-right">{l.status}</td>
                        <td className="px-2 py-1.5 text-right">{l.quantity}</td>
                        <td className="px-2 py-1.5 text-right">{inr(l.funds_used)}</td>
                        <td className="px-2 py-1.5 text-right text-[#FF6b62]">{num(l.sell_entry_price, 2)}</td>
                        <td className="px-2 py-1.5 text-right text-[#5b9bff]">{num(l.buy_entry_price, 2)}</td>
                        <td className="px-2 py-1.5 text-right">{num(l.entry_spread, 2)}</td>
                        <td className="px-2 py-1.5 text-right">{num(l.sell_exit_price, 2)}</td>
                        <td className="px-2 py-1.5 text-right">{num(l.buy_exit_price, 2)}</td>
                        <td className="px-2 py-1.5 text-right">{num(l.exit_spread, 2)}</td>
                        <td className={`px-2 py-1.5 text-right ${l.level_pnl >= 0 ? "text-[#00C805]" : "text-[#FF6b62]"}`}>{inr(l.level_pnl)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-500 whitespace-nowrap">{l.entry_time ? dateTime(l.entry_time) : "—"}</td>
                        <td className="px-2 py-1.5 text-right text-slate-500 whitespace-nowrap">{l.exit_time ? dateTime(l.exit_time) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "audit" && (
              <div className="space-y-1.5">
                {data.audit.length === 0 ? <div className="text-xs text-slate-600">No linked decisions.</div> :
                  data.audit.map((a) => (
                    <div key={a.id} className="term-panel-2 rounded-sm p-2 text-[11px] mono">
                      <div className="flex items-center justify-between"><span className="font-semibold text-slate-200">{a.event}</span><span className="text-slate-600">{istTime(a.ts)}</span></div>
                      {a.detail && <pre className="text-[10px] text-slate-400 whitespace-pre-wrap mt-1">{JSON.stringify(a.detail, null, 1)}</pre>}
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CyclesTable({ rows, onOpen }) {
  const heads = ["Started", "Start (IST)", "Completed (IST)", "Execution", "Trade", "Env", "Market", "Underlying",
    "SELL / BUY", "Levels", "Fills (B/S)", "Max Funds", "Realised P&L", "Exit", "Status"];
  return (
    <div className="overflow-auto rounded-sm border border-[#2A2F3A]" data-testid="trade-book-table">
      <table className="w-full text-[11px] mono border-collapse">
        <thead className="bg-[#1A1D24] text-slate-400"><tr>
          {heads.map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={heads.length} className="px-3 py-4 text-center text-slate-600" data-testid="trade-book-empty">No trade cycles match these filters. A cycle appears the moment Level 1 actually fills.</td></tr>
          ) : rows.map((e) => (
            <tr key={e.id} className="gz-row cursor-pointer" data-testid={`exec-row-${e.id}`}
              onClick={() => (e.product === "OPTIONS" && e.open_path ? window.location.assign(e.open_path) : onOpen(e.id))}>
              <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{istDate(e.started_date)}</td>
              <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{e.started_time || "—"}</td>
              <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{e.completed_date ? `${istDate(e.completed_date)} ${e.completed_time}` : "—"}</td>
              <td className="px-2 py-1.5 text-[#5b9bff] whitespace-nowrap" title={`Execution ${e.id}`} data-testid={`exec-label-${e.id}`}>
                {e.product === "OPTIONS" ? `${(e.display_name || e.trade_instance_name || "").split(" ").slice(0, 2).join(" ")} · #${String(e.id).slice(0, 6)}` : e.id}</td>
              <td className="px-2 py-1.5" title={e.display_name && e.display_name !== e.trade_instance_name ? `Created as ${e.trade_instance_name}` : undefined}>
                {e.display_name || e.trade_instance_name}</td>
              <td className="px-2 py-1.5">{e.trading_environment || e.environment}</td>
              <td className="px-2 py-1.5">{e.market}</td>
              <td className="px-2 py-1.5 text-fg font-semibold">{e.underlying}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">{e.sell_contract && <><span className="text-[#FF6b62]">{e.sell_contract}</span> / </>}<span className="text-[#5b9bff]">{e.buy_contract}</span></td>
              <td className="px-2 py-1.5">{e.levels_entered ?? "—"}</td>
              <td className="px-2 py-1.5">{e.fill_summary ? `${e.fill_summary.buy.fills} / ${e.fill_summary.sell.fills}` : "—"}</td>
              <td className="px-2 py-1.5">{inr(e.max_funds_used)}</td>
              <td className={`px-2 py-1.5 ${e.realised_pnl >= 0 ? "text-[#00C805]" : "text-[#FF6b62]"}`}>{inrPnl(e.realised_pnl, 2)}</td>
              <td className="px-2 py-1.5 text-slate-500" title={e.exit_reason || undefined}>{exitText(e.exit_reason)}</td>
              <td className="px-2 py-1.5"><span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${STATUS_COLORS[e.status] || "text-slate-400"}`}>{e.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SimulationJournal() {
  const [sim, setSim] = useState(null);
  useEffect(() => { api.get("/journal").then((r) => setSim(r.data)).catch(() => setSim([])); }, []);
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-[#FFCC00]" data-testid="sim-legacy-note">Legacy simulator history (engine: current_emergent_sim_v1) — ADMIN / dev only, never merged with V2.5 executions.</div>
      <div className="overflow-auto rounded-sm border border-[#2A2F3A]">
        <table className="w-full text-[11px] mono border-collapse">
          <thead className="bg-[#1A1D24] text-slate-400"><tr>{["Strategy", "Symbol", "P&L", "Closed (IST)"].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {(sim || []).length === 0 ? <tr><td colSpan={4} className="px-3 py-3 text-center text-slate-600">No legacy simulator records.</td></tr> :
              sim.map((j, i) => (
                <tr key={j.id || i} className="gz-row">
                  <td className="px-2 py-1.5">{j.strategy_name || j.name || "—"}</td>
                  <td className="px-2 py-1.5">{j.symbol || "—"}</td>
                  <td className={`px-2 py-1.5 ${(j.realized_pnl || 0) >= 0 ? "text-[#00C805]" : "text-[#FF6b62]"}`}>{inr(j.realized_pnl)}</td>
                  <td className="px-2 py-1.5 text-slate-500">{dateTime(j.closed_at)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TradeBook({ embedded = false, initialView = "cycles", scope = null }) {
  const { can } = useAuth();
  const canSim = can(P.SIMULATOR);
  const [view, setView] = useState(initialView);
  // History is the full record: it opens on every date. Fills open on today, like a broker's trade book.
  const [rf, setRf] = useState({ ...DEFAULT_REPORT_FILTERS, period: initialView === "cycles" && embedded ? "ALL" : DEFAULT_REPORT_FILTERS.period });
  const [cycles, setCycles] = useState(null);
  const [limit, setLimit] = useState(PAGE_STEP);
  const [fills, setFills] = useState(null);
  const [trades, setTrades] = useState([]);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/paper/v25/trades", { params: { market: "ALL" } })
      .then((r) => setTrades((r.data.trades || []).map((t) => [t.id, t.name || t.id]))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (view === "simulation") return;
    const base = reportParams(rf, { segment: rf.segment, limit });
    if (!base) return;
    const params = scope ? { ...base, environment: undefined, ...scopeParams(scope) } : base;
    const path = view === "cycles" ? "/trade-book/cycles" : "/trade-book/fills";
    api.get(path, { params })
      .then((r) => { (view === "cycles" ? setCycles : setFills)(r.data); setError(null); })
      .catch((e) => setError(detailText(e, "Could not load the Trade Book")));
  }, [view, rf, limit, scope?.product, scope?.environment]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); return visibleInterval(load, 10000); }, [load]);
  useEffect(() => { setLimit(PAGE_STEP); }, [view, rf]);

  // embedded in the Trading Book the tab (Trade Book = fills, History = cycles) chooses the view
  const tabs = embedded ? [] : [["cycles", "Trade Cycles"], ["fills", "Fills"], ...(canSim ? [["simulation", "SIMULATION / DEV"]] : [])];
  const summary = fills?.summary;

  return (
    <div className={embedded ? "space-y-3" : "p-4 space-y-3"}>
      {open && <DetailDrawer id={open} onClose={() => setOpen(null)} />}
      <div className="flex items-center justify-between flex-wrap gap-2">
        {embedded ? <span className="text-xs text-fg-secondary">{view === "cycles" ? "Trade cycles, newest first: open futures cycles and every completed cycle (realised P&L is net of charges)" : "Every fill (executed quantity), newest first"}</span> : (
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><BookOpen className="h-5 w-5 text-[#5b9bff]" /> Trade Book
          <span className="text-xs font-normal ml-2 text-slate-500">your executed trade cycles and fills</span>
        </h1>)}
        <div className="flex gap-1">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
              aria-pressed={view === k}
              className={`text-[11px] font-bold px-3 py-1 rounded-sm border ${view === k ? "bg-[var(--gz-blue)] border-transparent text-white" : "bg-surface-muted border-line text-fg-secondary hover:bg-hover"}`}>{l}</button>
          ))}
        </div>
      </div>

      {view !== "simulation" && <ReportFilters value={rf} onChange={setRf} trades={trades} showSegment hideEnvironment={!!scope} />}
      {error && view !== "simulation" && <div className="text-[11px] text-[#FF6b62]" data-testid="trade-book-error">{error}</div>}

      {view === "simulation" ? <SimulationJournal /> : view === "cycles" ? (
        !cycles ? <div className="text-xs text-slate-500">Loading…</div> : (
          <>
            <div className="text-[11px] text-slate-500 mono" data-testid="cycle-count">{cycles.count} trade cycle{cycles.count === 1 ? "" : "s"}</div>
            <CyclesTable rows={cycles.cycles} onOpen={setOpen} />
          </>
        )
      ) : (
        !fills ? <div className="text-xs text-slate-500">Loading…</div> : (
          <>
            <div className="flex flex-wrap gap-4 text-[11px] mono" data-testid="fill-summary">
              <PageCount shown={fills.count} total={fills.total ?? fills.count} noun={fills.count === 1 ? "fill" : "fills"}
                onMore={() => setLimit((l) => l + PAGE_STEP)} max={5000} testId="fill-count" />
              {summary && <>
                <span className="text-[#5b9bff]">BUY {summary.buy.fills} · qty {summary.buy.quantity} · {inr(summary.buy.value)}</span>
                <span className="text-[#FF6b62]">SELL {summary.sell.fills} · qty {summary.sell.quantity} · {inr(summary.sell.value)}</span>
              </>}
            </div>
            <FillsTable fills={fills.fills} />
          </>
        )
      )}
      <div className="text-[10px] text-slate-600">Dates and times are IST; the stored UTC timestamps are authoritative. Trade cycles are permanent — Reset never deletes history. Only actual fills create records. PAPER orders have internal ids only; LIVE fills come from Zerodha; SHADOW fills are simulated after Zerodha rejected the real order.</div>
    </div>
  );
}
